/**
 * src/components/spatial/polygonExtrusion.ts
 *
 * Which polygon extrudes between which two heights.
 *
 * Pulled out of `normalizeSpatialFeatures` for the same reason
 * `spatialFeatureDerivation` was: these are pure decisions about geometry
 * and stored metadata, they are easy to get wrong in ways a map does not
 * report, and checking them should not need a map, a canvas or a network.
 *
 * WHAT A "TIER" ACTUALLY IS. The stored name is `metadata.tiers` and it was
 * introduced for stepped towers, so the vocabulary suggests a setback: one
 * footprint sliced vertically. It never meant that. Each entry is a
 * polygon index with its own `min_height` and `height`, and nothing reads
 * it as a stack — so one spatial object can hold several unrelated
 * footprints, each extruding independently, and three towers of a complex
 * are as representable as five setbacks of one tower. This module calls
 * them parts, because that is what they are.
 *
 * ONE PART IS A PART. Earlier code treated a list of fewer than two as
 * empty, on the theory that a single-polygon building is fully described
 * by the object's own base and height. It is not: a polygon that starts at
 * 55m and stops at 85m needs somewhere to say so, and discarding its one
 * entry silently flattened it to the object extent. Length is not what
 * makes this metadata meaningful.
 */

/** One polygon's vertical extent. Wire shape — snake_case is the stored form. */
export type PolygonPart = {
  polygon_index: number;
  min_height: number;
  height: number;
};

/**
 * Flatten Polygon / MultiPolygon / GeometryCollection into an ordered list
 * of polygon parts (each `number[][][]` = rings).
 *
 * GeoLink often round-trips a saved MultiPolygon as a GeometryCollection of
 * Polygons; without this, part expansion is skipped and every part extrudes
 * from zero to the peak, which turns Atlantis's mid-tower bands into solid
 * columns.
 */
export function extractPolygonParts(geometry: unknown): number[][][][] | null {
  if (!geometry || typeof geometry !== 'object') return null;
  const g = geometry as {
    type?: string;
    coordinates?: unknown;
    geometries?: unknown[];
  };
  if (g.type === 'Polygon' && Array.isArray(g.coordinates)) {
    return [g.coordinates as number[][][]];
  }
  if (g.type === 'MultiPolygon' && Array.isArray(g.coordinates)) {
    return g.coordinates as number[][][][];
  }
  if (g.type === 'GeometryCollection' && Array.isArray(g.geometries)) {
    const out: number[][][][] = [];
    for (const part of g.geometries) {
      const nested = extractPolygonParts(part);
      if (nested) for (const p of nested) out.push(p);
    }
    return out.length > 0 ? out : null;
  }
  return null;
}

/** Polygon parts as a single geometry, without a pointless MultiPolygon. */
export function geometryOfParts(parts: number[][][][]): GeoJSON.Geometry {
  return parts.length === 1
    ? { type: 'Polygon', coordinates: parts[0] }
    : { type: 'MultiPolygon', coordinates: parts };
}

/** Coerce GeometryCollection-of-Polygons → MultiPolygon for stable indices. */
export function asMultiPolygonGeometry(geometry: unknown): GeoJSON.Geometry | null {
  const parts = extractPolygonParts(geometry);
  if (!parts || parts.length === 0) return null;
  return geometryOfParts(parts);
}

/**
 * Parse a stored part list.
 *
 * Accepts a single entry: see the header. Entries that do not describe a
 * polygon extent at all — unparseable numbers, negative indices — are
 * dropped individually rather than invalidating the list, because a
 * partially readable list still places the parts it can.
 */
export function parsePartsArray(raw: unknown): PolygonPart[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const out: PolygonPart[] = [];
  for (const t of raw) {
    if (!t || typeof t !== 'object') continue;
    const r = t as Record<string, unknown>;
    const idx = Number(r.polygon_index);
    const mn  = Number(r.min_height);
    const mx  = Number(r.height);
    if (!Number.isFinite(idx) || !Number.isFinite(mn) || !Number.isFinite(mx)) continue;
    if (idx < 0) continue;
    out.push({ polygon_index: idx, min_height: mn, height: mx });
  }
  return out;
}

/** Highest polygon_index referenced by a part list. */
export function maxPartIndex(parts: PolygonPart[]): number {
  return parts.reduce((m, t) => (t.polygon_index > m ? t.polygon_index : m), 0);
}

/**
 * True when every part's index resolves to a polygon inside `geometry`.
 *
 * GeoLink round-trips can collapse overlapping footprints (a 45-part
 * Atlantis MultiPolygon comes back as a GeometryCollection whose
 * MultiPolygon has 11 parts) while the stored list still references all 45
 * indices. Such a pair can never render and must never be trusted.
 */
export function partsFitGeometry(parts: PolygonPart[], geometry: unknown): boolean {
  if (parts.length === 0) return false;
  const polys = extractPolygonParts(geometry);
  if (!polys || polys.length === 0) return false;
  return maxPartIndex(parts) < polys.length;
}

/**
 * A footprint's identity, stable across the ways GeoLink restates it.
 *
 * This exists to tell two situations apart that the old code could not:
 * GeoLink returning the same building in a lossier form than it was saved
 * in, and somebody editing the building's geometry. The first must not
 * invalidate cached parts, because surviving it is the entire reason the
 * cache exists. The second must invalidate them immediately, because
 * otherwise an intentional edit is silently reverted to the shape it
 * replaced — which is exactly what happened to the Kuwait Towers
 * experiment.
 *
 * So the signature is the overall bounding box, rounded. Reordering parts
 * does not change it. Collapsing overlapping parts does not change it,
 * because the hull is unchanged. Replacing one tower with three does.
 *
 * Deliberately not a hash of every coordinate: that would make GeoLink's
 * own re-encoding look like an edit, and the cache would evaporate on the
 * first refresh for every object it was built to protect.
 */
export function geometrySignature(geometry: unknown): string | null {
  const polys = extractPolygonParts(geometry);
  if (!polys || polys.length === 0) return null;

  let minX = Infinity; let minY = Infinity;
  let maxX = -Infinity; let maxY = -Infinity;
  for (const rings of polys) {
    for (const ring of rings) {
      for (const pt of ring) {
        const x = Number(pt?.[0]);
        const y = Number(pt?.[1]);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;

  // Five places is about a metre — finer than any edit worth noticing and
  // coarser than the float noise a re-encode introduces.
  const r = (n: number) => n.toFixed(5);
  return `${r(minX)},${r(minY)},${r(maxX)},${r(maxY)}`;
}

/** A remembered (geometry, parts) pair, plus what the server last looked like. */
export type CachedExtrusion = {
  geometry: GeoJSON.Geometry;
  parts: PolygonPart[];
  /**
   * Signature of the geometry the server was returning when this entry was
   * last confirmed. `null` means the entry came from a local save and the
   * server has not echoed yet, so there is nothing to disagree with.
   */
  wireSignature?: string | null;
};

/**
 * Where the resolved geometry and parts came from.
 *
 * `wire` — the payload described itself consistently; nothing was borrowed.
 * `wire-geometry-cached-parts` — the server's footprint with remembered
 *   heights laid over it. Preferred over `cached-pair` whenever the indices
 *   still line up, because it keeps the server authoritative about shape.
 * `cached-pair` — both borrowed, for a server response too lossy to index
 *   into. Only when the server still looks like it did when this was saved.
 * `cached-geometry` — the server sent no polygons at all.
 * `none` — nothing to extrude per-part; the caller falls back to the
 *   object's own base and height over the whole geometry.
 */
export type ExtrusionSource =
  | 'wire'
  | 'wire-geometry-cached-parts'
  | 'cached-pair'
  | 'cached-geometry'
  | 'none';

export type ExtrusionPlan = {
  /** Geometry to render. */
  geometry: unknown;
  /** Per-polygon extents, or `[]` when the object extent covers everything. */
  parts: PolygonPart[];
  source: ExtrusionSource;
  /**
   * True when the cached entry disagreed with the server about the
   * building's shape. The caller must forget it — it describes a building
   * that no longer exists.
   */
  staleCache: boolean;
  /**
   * Signature to record against the cache entry, when the entry is being
   * kept and had not yet seen the server. `null` when there is nothing to
   * record.
   */
  adoptWireSignature: string | null;
};

/**
 * What to do with the remembered pair when a save or an echo arrives.
 *
 * `store` — keep this pair. `resetSignature` marks it as not yet seen by
 *   the server, which is true of a user's save and false of an echo.
 * `forget` — drop the pair entirely.
 * `remember-geometry-only` — no extents to keep, but the footprint is
 *   worth having as a fallback.
 * `skip` — leave everything as it is.
 */
export type CacheWrite =
  | { action: 'store'; resetSignature: boolean }
  | { action: 'forget'; rememberGeometry: boolean }
  | { action: 'remember-geometry-only' }
  | { action: 'skip' };

/**
 * Whether an incoming (geometry, parts) pair should replace the
 * remembered one.
 *
 * The case that matters is a save with no extents at all. That used to
 * fall through to "remember the geometry if we have nothing better",
 * which left the old extents sitting in the cache — so a user who cleared
 * them and saved got them back on the next refresh, and had no way to
 * undo an import short of clearing site data. A deliberate save with no
 * extents is now a deletion.
 */
export function resolveCacheWrite(input: {
  parts: PolygonPart[];
  geometry: GeoJSON.Geometry | undefined;
  /** True only for a real user save, not a server echo or a list re-seed. */
  authoritative: boolean;
  /** Extents currently remembered for this object, if any. */
  existingPartCount: number;
}): CacheWrite {
  const { parts, geometry, authoritative, existingPartCount } = input;

  if (parts.length > 0) {
    // Extents are only meaningful against the geometry they were authored
    // with, so a pair that cannot index into itself is not worth keeping.
    if (!geometry || !partsFitGeometry(parts, geometry)) return { action: 'skip' };
    // A server echo may not downgrade a richer import pair: GeoLink
    // collapses overlapping footprints on save, so its echo legitimately
    // describes fewer parts than were sent.
    if (!authoritative && existingPartCount > parts.length) return { action: 'skip' };
    return { action: 'store', resetSignature: authoritative };
  }

  if (authoritative) return { action: 'forget', rememberGeometry: !!geometry };

  return geometry ? { action: 'remember-geometry-only' } : { action: 'skip' };
}

/**
 * Decide what to render, and whether a remembered pair may still be trusted.
 *
 * The order matters and each step earns its place:
 *
 *   1. A self-consistent wire pair wins outright. The server is
 *      authoritative and said something coherent.
 *   2. Otherwise, try the server's geometry with remembered parts. This is
 *      the step the old code lacked, and the one that stops an edit being
 *      reverted: borrowing heights is safe, borrowing a footprint is not.
 *   3. Only if the parts cannot index into the server's geometry is the
 *      remembered footprint substituted — and only while the server still
 *      looks like it did when the pair was saved.
 *   4. A response with no polygons at all falls back to the remembered
 *      footprint, which is better than an unpainted building.
 *   5. Anything else extrudes as one piece.
 */
export function resolveExtrusion(input: {
  wireGeometry: unknown;
  wireParts: PolygonPart[];
  cached: CachedExtrusion | null;
}): ExtrusionPlan {
  const { wireGeometry, wireParts, cached } = input;

  const wireSignature = geometrySignature(wireGeometry);

  // Has the building changed shape since this entry was remembered? An
  // entry that has never seen the server (`null`) cannot disagree with it.
  const staleCache = !!cached
    && cached.wireSignature != null
    && wireSignature != null
    && cached.wireSignature !== wireSignature;

  const usable = staleCache ? null : cached;

  const nothingRecorded: Pick<ExtrusionPlan, 'staleCache' | 'adoptWireSignature'> = {
    staleCache,
    adoptWireSignature: null,
  };

  // The signature to write back when we keep an entry that has not yet met
  // the server. Recording it is what makes a later edit detectable.
  const adopt = usable && usable.wireSignature == null ? wireSignature : null;

  // 1. Wire describes itself.
  if (partsFitGeometry(wireParts, wireGeometry)) {
    return {
      geometry: wireGeometry,
      parts: wireParts,
      source: 'wire',
      ...nothingRecorded,
    };
  }

  // 2. Server's shape, remembered heights.
  if (usable && partsFitGeometry(usable.parts, wireGeometry)) {
    return {
      geometry: wireGeometry,
      parts: usable.parts,
      source: 'wire-geometry-cached-parts',
      staleCache,
      adoptWireSignature: adopt,
    };
  }

  // 3. Both remembered, for a response too lossy to index into.
  if (usable && partsFitGeometry(usable.parts, usable.geometry)) {
    return {
      geometry: usable.geometry,
      parts: usable.parts,
      source: 'cached-pair',
      staleCache,
      adoptWireSignature: adopt,
    };
  }

  // 4. Nothing to render from the server at all.
  if (usable && !extractPolygonParts(wireGeometry)) {
    return {
      geometry: usable.geometry,
      parts: [],
      source: 'cached-geometry',
      staleCache,
      adoptWireSignature: adopt,
    };
  }

  // 5. One piece, object extent.
  return {
    geometry: wireGeometry,
    parts: [],
    source: 'none',
    ...nothingRecorded,
  };
}
